import { Trans } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Box } from '@startuptools/ui/Box';
import { Stack } from '@startuptools/ui/Stack';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { Alert } from '@startuptools/ui/Alert';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { range } from 'lodash-es';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useCompanySetMeetingFrequencyMutation, useCompanySuspenseQuery } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlDayOfWeek, GqlMeetingBase } from '../../graphql/base-types.graphql';
import { toast } from '@startuptools/ui/toast/use-toast';

const weekDays = [
  { value: GqlDayOfWeek.Monday, text: <Trans id="Monday" /> },
  { value: GqlDayOfWeek.Tuesday, text: <Trans id="Tuesday" /> },
  { value: GqlDayOfWeek.Wednesday, text: <Trans id="Wednesday" /> },
  { value: GqlDayOfWeek.Thursday, text: <Trans id="Thursday" /> },
  { value: GqlDayOfWeek.Friday, text: <Trans id="Friday" /> },
  { value: GqlDayOfWeek.Saturday, text: <Trans id="Saturday" /> },
  { value: GqlDayOfWeek.Sunday, text: <Trans id="Sunday" /> },
] as const;

const base = [
  { value: GqlMeetingBase.Day, text: <Trans id="Day" /> },
  { value: GqlMeetingBase.Week, text: <Trans id="Week" /> },
  { value: GqlMeetingBase.Month, text: <Trans id="Month" /> },
  { value: GqlMeetingBase.Quarter, text: <Trans id="Quarter" /> },
  { value: GqlMeetingBase.Tertial, text: <Trans id="Tertial" /> },
  { value: GqlMeetingBase.Halfyear, text: <Trans id="Halfyear" /> },
  { value: GqlMeetingBase.Year, text: <Trans id="Year" /> },
] as const;

type FormValues = {
  base: GqlMeetingBase;
  dayOfWeek: GqlDayOfWeek;
  frequency: number;
};

export const BoardMeetingFrequency = () => {
  const companyId = useCompanyId();
  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetMeetingFreq] = useCompanySetMeetingFrequencyMutation();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      base: company.boardSettings?.base ?? GqlMeetingBase.Week,
      dayOfWeek: company.boardSettings?.dayOfWeek ?? GqlDayOfWeek.Monday,
      frequency: company.boardSettings?.frequency ?? 8,
    },
  });

  const submit: SubmitHandler<FormValues> = async data => {
    try {
      await doSetMeetingFreq({ variables: { companyId, input: data } });
    } catch {
      toast({
        severity: 'danger',
        title: <Trans id="Error!" />,
        description: <Trans id="Could not save board meeting settings, please try again" />,
      });
      return;
    }
    toast({
      severity: 'success',
      title: <Trans id="Success!" />,
      description: <Trans id="Board meeting settings was saved" />,
    });
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(submit)}>
        <Stack gap={2}>
          <Typography level="h2">
            <Trans id="Board Meeting Frequency" />
          </Typography>
          <Alert variant="outlined" color="primary" startDecorator={<InfoOutlinedIcon />}>
            <Trans id="These settings dictate what date are suggested when creating new board meetings" />
          </Alert>
          <Typography level="title-lg">
            <Trans id="Board meetings shall be held..." />
          </Typography>
          <Stack direction="row" gap={1}>
            <Box>
              <Controller
                control={control}
                name="frequency"
                render={({ field: { onChange, ...field } }) => (
                  <Select {...field} onChange={(_, value) => onChange(value)} sx={{ width: 200 }}>
                    <Option value={1}>
                      <Trans id="every" />
                    </Option>
                    <Option value={2}>
                      <Trans id="every other" />
                    </Option>
                    {range(3, 31).map(n => (
                      <Option key={n} value={n}>
                        <Trans
                          id="every {n, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}"
                          values={{ n }}
                        />
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name="base"
                render={({ field: { onChange, ...field } }) => (
                  <Select {...field} onChange={(_, value) => onChange(value)} sx={{ width: 150 }}>
                    {base.map(({ value, text }) => (
                      <Option key={value} value={value}>
                        {text}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </Box>
          </Stack>

          <Typography level="title-lg">
            <Trans id="On a..." />
          </Typography>
          <Controller
            control={control}
            name="dayOfWeek"
            render={({ field }) => (
              <RadioGroup {...field}>
                {weekDays.map(({ value, text }) => (
                  <Radio key={value} value={value} label={text} />
                ))}
              </RadioGroup>
            )}
          />
          <Box>
            <Button type="submit">
              <Trans id="Save" />
            </Button>
          </Box>
        </Stack>
      </form>
    </Card>
  );
};
